import { createFileRoute } from "@tanstack/react-router";
import { useState, useRef, useEffect } from "react";
import { z } from "zod";
import Compressor from "compressorjs";

export const Route = createFileRoute("/admin/config/editFoto")({
  component: RouteComponent,
  validateSearch: z.object({ fotografo_id: z.number().optional() }),
});

function RouteComponent() {
  return <ImageCropper />;
}

type Point = { x: number; y: number };

const TARGET_WIDTH = 200;
const TARGET_HEIGHT = 266; // 200 * (4/3)

const aspectRatio = 3 / 4;

export default function ImageCropper() {
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [imageElement, setImageElement] = useState<HTMLImageElement | null>(
    null
  );

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const selectionCanvasRef = useRef<HTMLCanvasElement>(null);

  const [isSelecting, setIsSelecting] = useState(false);
  const [startPoint, setStartPoint] = useState<Point | null>(null);
  const [selectionRect, setSelectionRect] = useState<{
    x: number;
    y: number;
    width: number;
    height: number;
  } | null>(null);

  const [croppedBlobUrl, setCroppedBlobUrl] = useState<string | null>(null);

  // Load image from file input
  function onFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const url = URL.createObjectURL(file);
    setImageSrc(url);
    setSelectionRect(null);
    setCroppedBlobUrl(null);
  }

  // Draw image on base canvas when imageSrc changes
  useEffect(() => {
    if (!imageSrc) return;
    const img = new Image();
    img.onload = () => {
      setImageElement(img);
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      // Resize canvas to fit image (max width 600 for display)
      const maxDisplayWidth = 600;
      let scale = 1;
      if (img.width > maxDisplayWidth) scale = maxDisplayWidth / img.width;
      canvas.width = img.width * scale;
      canvas.height = img.height * scale;

      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

      // Clear selection canvas as well
      const selCanvas = selectionCanvasRef.current;
      if (selCanvas) {
        selCanvas.width = canvas.width;
        selCanvas.height = canvas.height;
        const selCtx = selCanvas.getContext("2d");
        if (selCtx) selCtx.clearRect(0, 0, selCanvas.width, selCanvas.height);
      }
    };
    img.src = imageSrc;
  }, [imageSrc]);

  // Handle mouse events to draw a 3:4 ratio rectangle selection
  function onMouseDown(e: React.MouseEvent<HTMLCanvasElement>) {
    if (!imageElement) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    setStartPoint({ x, y });
    setIsSelecting(true);
  }

  function onMouseMove(e: React.MouseEvent<HTMLCanvasElement>) {
    if (!isSelecting || !startPoint || !imageElement) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;

    // Calculate width and height based on fixed aspect ratio 3:4
    let width = mouseX - startPoint.x;
    let height = mouseY - startPoint.y;

    // Determine direction to maintain correct rect drawing
    const dirX = width >= 0 ? 1 : -1;
    const dirY = height >= 0 ? 1 : -1;

    width = Math.abs(width);
    height = Math.abs(height);

    // Adjust width or height to maintain 3:4 ratio
    if (width / height > aspectRatio) {
      width = height * aspectRatio;
    } else {
      height = width / aspectRatio;
    }

    // Calculate top-left corner based on drag direction
    const x = dirX > 0 ? startPoint.x : startPoint.x - width;
    const y = dirY > 0 ? startPoint.y : startPoint.y - height;

    // Clamp selection inside canvas bounds
    const canvas = e.currentTarget;
    const maxX = canvas.width;
    const maxY = canvas.height;

    const clampedX = Math.min(Math.max(x, 0), maxX);
    const clampedY = Math.min(Math.max(y, 0), maxY);
    const clampedWidth = Math.min(width, maxX - clampedX);
    const clampedHeight = Math.min(height, maxY - clampedY);

    const selection = {
      x: clampedX,
      y: clampedY,
      width: clampedWidth,
      height: clampedHeight,
    };

    setSelectionRect(selection);

    // Draw selection rectangle on overlay canvas
    const selCanvas = selectionCanvasRef.current;
    if (!selCanvas) return;
    const selCtx = selCanvas.getContext("2d");
    if (!selCtx) return;

    selCtx.clearRect(0, 0, selCanvas.width, selCanvas.height);
    selCtx.strokeStyle = "red";
    selCtx.lineWidth = 2;
    selCtx.setLineDash([6]);
    selCtx.strokeRect(
      selection.x,
      selection.y,
      selection.width,
      selection.height
    );
  }

  function onMouseUp() {
    setIsSelecting(false);
  }

  // Crop and scale the selected area
  function cropAndScale() {
    if (!selectionRect || !imageElement) return;

    // Calculate scale ratio from displayed image to original image
    const scaleX = imageElement.width / canvasRef.current!.width;
    const scaleY = imageElement.height / canvasRef.current!.height;

    // Calculate crop rect on original image
    const sx = selectionRect.x * scaleX;
    const sy = selectionRect.y * scaleY;
    const sw = selectionRect.width * scaleX;
    const sh = selectionRect.height * scaleY;

    // Create offscreen canvas for cropping and scaling
    const offCanvas = document.createElement("canvas");
    offCanvas.width = TARGET_WIDTH;
    offCanvas.height = TARGET_HEIGHT;
    const offCtx = offCanvas.getContext("2d");
    if (!offCtx) return;

    offCtx.clearRect(0, 0, TARGET_WIDTH, TARGET_HEIGHT);
    offCtx.drawImage(
      imageElement,
      sx,
      sy,
      sw,
      sh,
      0,
      0,
      TARGET_WIDTH,
      TARGET_HEIGHT
    );

    // Convert to blob (jpeg) and compress with CompressorJS
    offCanvas.toBlob((blob) => {
      if (!blob) return;
      new Compressor(blob, {
        quality: 1,
        mimeType: "image/jpeg",
        success(result) {
          // Create URL for download & preview
          const url = URL.createObjectURL(result);
          setCroppedBlobUrl(url);
        },
        error(err) {
          console.error("Compression error:", err);
        },
      });
    }, "image/jpeg");
  }

  // Cleanup blob URL when component unmounts or new image selected
  useEffect(() => {
    return () => {
      if (croppedBlobUrl) {
        URL.revokeObjectURL(croppedBlobUrl);
      }
    };
  }, [croppedBlobUrl]);

  return (
    <div style={{ fontFamily: "sans-serif", maxWidth: 640, margin: "auto" }}>
      <input type="file" accept="image/*" onChange={onFileChange} />
      <div style={{ position: "relative", marginTop: 20 }}>
        <canvas
          ref={canvasRef}
          style={{ display: imageSrc ? "block" : "none" }}
        />
        <canvas
          ref={selectionCanvasRef}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            pointerEvents: "auto",
            cursor: "crosshair",
          }}
          onMouseDown={onMouseDown}
          onMouseMove={onMouseMove}
          onMouseUp={onMouseUp}
          onMouseLeave={onMouseUp}
        />
      </div>
      <button
        onClick={cropAndScale}
        disabled={!selectionRect}
        style={{ marginTop: 10, padding: "8px 16px", fontSize: 16 }}
      >
        Crop & Compress
      </button>

      {croppedBlobUrl && (
        <div style={{ marginTop: 20 }}>
          <h3>Result:</h3>
          <img
            src={croppedBlobUrl}
            alt="Cropped"
            width={TARGET_WIDTH}
            height={TARGET_HEIGHT}
            style={{ display: "block", border: "1px solid #ccc" }}
          />
          <a
            href={croppedBlobUrl}
            download="cropped-image.jpg"
            style={{
              display: "inline-block",
              marginTop: 10,
              padding: "8px 16px",
              backgroundColor: "#007bff",
              color: "white",
              textDecoration: "none",
              borderRadius: 4,
            }}
          >
            Download Cropped Image
          </a>
        </div>
      )}
    </div>
  );
}
